// routesApi — typed CRUD for /v1/routes/* (Route 9).
import { apiFetch } from "../../lib/api-client.js";

export type RouteStatus = "active" | "archived";

export interface RouteRow {
  id: string;
  tenant_id: string;
  site_id: string;
  site_name: string | null;
  name: string;
  is_template: number;
  status: RouteStatus;
  cadence_minutes: number | null;
  expected_duration_seconds: number | null;
  location_count: number;
  /** Number of locations on this route's `route_locations` that have
   *  ZERO active `location_task_assignments` rows (route_id=this,
   *  location_id=that). Non-zero means the scan wizard at those points
   *  will fall through to the empty-state — admin needs to派發任務. */
  assignment_gap_count: number;
  created_at: string;
  updated_at: string;
}

export interface RouteListResponse { items: RouteRow[]; correlation_id: string | null }

export class RoutesApiError extends Error {
  status: number; code: string; correlationId: string | null; fields?: string[]; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, fields?: string[], upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.fields = fields; this.upstreamMessage = upstreamMessage;
  }
}
export function friendlyRoutesError(err: unknown): string {
  if (err instanceof RoutesApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改路線。";
    if (err.status === 404) return "找不到此路線。";
    if (err.status === 400 && err.code === "tenant_id_required") return "尚未選定操作的單位。";
    if (err.status === 400 && err.code === "site_not_found") return "選定的案場不存在。";
    if (err.status === 400 && err.code === "invalid_body") return `欄位填寫不完整：${(err.fields ?? []).join("、")}。`;
    return "路線資料暫時無法存取。";
  }
  return "連線中斷，請檢查網路後再試。";
}
async function asError(r: Response): Promise<RoutesApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let fields: string[] | undefined; let msg: string | undefined;
  try { const b = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[]; message?: string }; code = b.error ?? code; cid = b.correlation_id ?? cid; fields = b.fields; msg = b.message; } catch {}
  return new RoutesApiError(r.status, code, cid, fields, msg);
}

export interface RoutesQuery { tenantId?: string | null; siteId?: string | null; q?: string | null; status?: RouteStatus | "" | null }
export async function listRoutes(p: RoutesQuery = {}): Promise<RouteListResponse> {
  const qs = new URLSearchParams();
  if (p.tenantId) qs.set("tenant_id", p.tenantId);
  if (p.siteId) qs.set("site_id", p.siteId);
  if (p.q) qs.set("q", p.q);
  if (p.status) qs.set("status", p.status);
  const r = await apiFetch(`/v1/routes${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as RouteListResponse;
}

export interface CreateRouteBody {
  name: string; site_id: string; cadence_minutes?: number | null;
  expected_duration_seconds?: number | null; status?: RouteStatus;
}
export async function createRoute(b: CreateRouteBody, tenantId?: string | null): Promise<{ id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/routes${qs}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(b), credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}
export type PatchRouteBody = Partial<CreateRouteBody>;
export async function patchRoute(id: string, b: PatchRouteBody, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/routes/${encodeURIComponent(id)}${qs}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(b), credentials: "include" });
  if (!r.ok) throw await asError(r);
}
export async function archiveRoute(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/routes/${encodeURIComponent(id)}${qs}`, { method: "DELETE", credentials: "include" });
  if (!r.ok) throw await asError(r);
}

export interface RouteLocationRow {
  location_id: string;
  sequence: number;
  expected_dwell_seconds: number | null;
  code: string;
  label: string;
  location_hint: string | null;
  /** Number of active `location_task_assignments` for this exact
   *  (route_id, location_id) pair. Zero means the admin added the
   *  location to the route's path but never wired a task to fire here
   *  — the scan wizard will not show this route as active at this point. */
  active_assignment_count: number;
}
export async function listRouteLocations(
  routeId: string, tenantId?: string | null,
): Promise<{ items: RouteLocationRow[] }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/routes/${encodeURIComponent(routeId)}/locations${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: RouteLocationRow[] };
}

export interface PutRouteLocationsItem { location_id: string; expected_dwell_seconds?: number | null }
export async function putRouteLocations(
  routeId: string, items: PutRouteLocationsItem[], tenantId?: string | null,
): Promise<{ ok: true; count: number }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/routes/${encodeURIComponent(routeId)}/locations${qs}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ items }),
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; count: number };
}

// ── Route preview (navigation graph data bundle) ─────────────────────

export interface RoutePreviewLocation {
  location_id: string;
  sequence: number;
  expected_dwell_seconds: number | null;
  code: string;
  label: string;
  location_hint: string | null;
  /** Floor this location is shown on. Derived server-side from the
   *  active map_marker linked to the location (NOT from the deprecated
   *  locations.floor_id column). null when the location has no
   *  active marker placed on any floor yet — frontend renders a
   *  "尚未標記" group with an actionable hint in that case. */
  floor_id: string | null;
  /** Building of the marker's floor (resolved server-side via
   *  building_floors). null when floor_id is null. */
  building_id: string | null;
  lat: number | null;
  lon: number | null;
  geofence_radius_m: number | null;
  /** Pin position on the floor's CURRENT layout version, if placed. */
  position_x: number | null;
  position_y: number | null;
  layout_version_id: string | null;
}

export interface RoutePreviewFloor {
  id: string;
  building_id: string;
  floor_no: number;
  floor_name: string;
  display_label: string | null;
  layout_version: {
    id: string;
    version_no: number;
    /** Parsed NormalizedLayout — see packages/shared/src/openai/layout-parser.ts. */
    normalized_json: unknown;
    viewbox_json: unknown;
  } | null;
}

export interface RoutePreviewResponse {
  route: {
    id: string;
    tenant_id: string;
    site_id: string;
    name: string;
    sequence_strict: number;
    status: string;
  };
  locations: RoutePreviewLocation[];
  floors: RoutePreviewFloor[];
  correlation_id: string | null;
}

export async function getRoutePreview(routeId: string, tenantId?: string | null): Promise<RoutePreviewResponse> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/routes/${encodeURIComponent(routeId)}/preview${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as RoutePreviewResponse;
}
